import "@/styles/index.css"
import "@/console-interceptor" // Must be imported early to capture all console logs and errors
import "@/i18n" // Must be initialized before any module that resolves translations at import time
import React, { Suspense } from "react"
import ReactDOM from "react-dom/client"
import { RelayEnvironmentProvider } from "react-relay"
import { createBrowserRouter, Outlet, RouterProvider } from "react-router"
import { IconContext } from "@phosphor-icons/react"
import { getUser } from "@/apis/bff"
import { App, rootLoader } from "@/app"
import { Confirmation } from "@/components/ui/confirmation"
import { Toaster } from "@/components/ui/toast"
import { initializeGrafanaFaroMonitoring } from "@/grafana-faro"
import { Admin, hasNavigationItemAccess, subNavigation } from "@/layouts/admin"
import { WaitingRoomProvider } from "@/layouts/default"
import { RootErrorBoundary } from "@/layouts/error"
import { PatientLayoutIndependent } from "@/layouts/patient-layout"
import { Advanced } from "@/modules/admin/advanced/advanced"
import { AgentPlannings } from "@/modules/admin/agent-plannings/agent-plannings"
import { AgentSkills } from "@/modules/admin/agent-skills/agent-skills"
import { BetterDoc } from "@/modules/admin/better-doc/better-doc"
import { Capacity } from "@/modules/admin/capacity/capacity"
import { News } from "@/modules/admin/news/news"
import { SasisLookup } from "@/modules/admin/sasis-lookup/sasis-lookup"
import { TreatmentPlanTemplatesCreateEdit } from "@/modules/admin/treatment/create-edit/treatment-plan-templates-create-edit"
import { TreatmentPlanTemplates } from "@/modules/admin/treatment/treatment-plan-templates"
import { Chats } from "@/modules/chats/chats"
import { LoggedHinEpd } from "@/modules/hin/logged-hin-epd"
import { LoggedHinSigning } from "@/modules/hin/logged-hin-signing"
import { PatientAgenda } from "@/modules/patients/agenda"
import { PatientDetail } from "@/modules/patients/detail"
import { History } from "@/modules/patients/history"
import { PatientInformation } from "@/modules/patients/information"
import { Patients } from "@/modules/patients/patients"
import { PdfLinkWarning } from "@/modules/pdf-link"
import { PatientRedirect } from "@/modules/redirect/patient-redirect"
import { ShiftPlanOverview } from "@/modules/shift-plan-overview/shift-plan-overview"
import { SpeechToTextTestPage } from "@/modules/speech-to-text-test/speech-to-text-test-page"
import { LoggedGenesysTelephony } from "@/modules/telephony/logged-genesys-telephony"
import { DummyVideoCallLayout } from "@/modules/video-call/components/dummy-call/dummy-video-call-layout"
import { VideoCallLayout } from "@/modules/video-call/video-call-layout"
import { WaitingRoom } from "@/modules/waiting-room/waiting-room"
import { RelayEnvironment } from "@/relay-environment"
import { handleInsensitiveInboundCallParam } from "@/stores/useCallContextStore"

const adminLoader = async () => {
  try {
    const { user } = await getUser()
    const existingChild = subNavigation
      .flatMap((sn) => sn.children)
      .find((sn) => window.location.pathname.includes(sn.to))

    if (existingChild && !hasNavigationItemAccess(existingChild, user)) {
      router.navigate("/")
    }
  } catch (error) {
    console.error("Error:", error)
  }
  return null
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    id: "root",
    loader: rootLoader,
    errorElement: <RootErrorBoundary />,
    children: [
      {
        path: "/",
        element: <WaitingRoomProvider />,
        children: [
          { index: true, element: <WaitingRoom /> },
          {
            path: "chats/:threadId?",
            element: <Chats />,
            children: [
              {
                index: true,
                element: <PatientDetail />,
              },
              {
                path: "agenda",
                element: <PatientAgenda />,
              },
              {
                path: "information",
                element: <PatientInformation />,
              },
              {
                path: "history",
                element: <History />,
              },
            ],
          },
          {
            path: "admin",
            element: <Outlet />,
            loader: adminLoader,
            children: [
              { index: true, element: <Admin /> },
              {
                path: "news",
                element: <News />,
              },
              {
                path: "capacity",
                element: <Capacity />,
              },
              {
                path: "agent-skills",
                element: <AgentSkills />,
              },
              {
                path: "agent-plannings",
                element: <AgentPlannings />,
              },
              {
                path: "sasis-lookup",
                element: <SasisLookup />,
              },
              {
                path: "better-doc",
                element: <BetterDoc />,
              },
              {
                path: "treatment-plan-templates",
                element: <TreatmentPlanTemplates />,
              },
              {
                path: "treatment-plan-templates/new",
                element: <TreatmentPlanTemplatesCreateEdit />,
              },
              {
                path: "treatment-plan-templates/edit/:templateId",
                element: <TreatmentPlanTemplatesCreateEdit />,
              },
              {
                path: "advanced",
                element: <Advanced />,
              },
              {
                path: "call-transcript-simulator",
                element: <SpeechToTextTestPage />,
              },
            ],
          },
          { path: "patients", element: <Patients /> },
          { path: "shift-plan-overview", element: <ShiftPlanOverview /> },
        ],
      },
      {
        path: "patients/:country/:patientId",
        element: <PatientLayoutIndependent />,
        children: [
          {
            index: true,
            element: <PatientDetail />,
          },
          {
            path: "agenda",
            element: <PatientAgenda />,
          },
          {
            path: "information",
            element: <PatientInformation />,
          },
          {
            path: "history",
            element: <History />,
          },
        ],
      },
      {
        path: "video-call",
        element: <VideoCallLayout />,
      },
      {
        path: "dummy-video-call",
        element: <DummyVideoCallLayout />,
      },
      {
        path: "/hin-sign-logged-in",
        element: <LoggedHinSigning />,
      },
      {
        path: "/hin-epd-logged-in",
        element: <LoggedHinEpd />,
      },
      {
        path: "/genesys-telephony-logged-in",
        element: <LoggedGenesysTelephony />,
      },
      {
        path: "/pdf-link",
        element: <PdfLinkWarning />,
      },
      {
        path: "redirect/patient/:country/:patientId",
        element: <PatientRedirect />,
      },
    ],
  },
])

initializeGrafanaFaroMonitoring(router)
handleInsensitiveInboundCallParam()

ReactDOM.createRoot(document.getElementById("root")!, {
  identifierPrefix: "sofia-app-",
}).render(
  <React.StrictMode>
    <Suspense fallback={<p>loading...</p>}>
      <RelayEnvironmentProvider environment={RelayEnvironment}>
        <IconContext.Provider value={{ size: "1.5rem" }}>
          <RouterProvider router={router} />
          <Toaster />
          <Confirmation />
        </IconContext.Provider>
      </RelayEnvironmentProvider>
    </Suspense>
  </React.StrictMode>,
)
